import React, { useState, useEffect } from "react";
import {
    Box,
    Flex,
    Input,
    Select,
    InputGroup,
    InputLeftElement,
    InputRightElement
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import Flag from "react-world-flags";
import { AsYouType } from "libphonenumber-js";
import { getCountryTelCode } from "./countries";
import { FaPhoneAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";

export default function PhoneNumberInput({
    size,
    value,
    country,
    options,
    onChange,
    placeholder,
    lang,
    ...rest
}) {
    const { t } = useTranslation(['translation']);
    let [number, setNumber] = useState(value || "");
    let [selectedCountry, setSelectedCountry] = useState(country || "");
    let [countryCode, setCountryCode] = useState(
        getCountryTelCode(country) || "egypt"
    );

    useEffect(() => {
        setSelectedCountry(country);
        setCountryCode(getCountryTelCode(country));
    }, [country]);

    // A number typed with its own "+" prefix is already international, so the
    // picker's dial code is not prepended (it would produce "+20+1 ...").
    const withDialCode = (code, value) =>
        value.trim().startsWith("+") ? value.trim() : `${code}${value}`;

    const onCountryChange = e => {
        let value = e.target.value;
        let code = getCountryTelCode(value);
        let parsedNumber = new AsYouType().input(withDialCode(code, number));
        setCountryCode(code);
        setSelectedCountry(value);
        onChange(parsedNumber);
    };

    const onPhoneNumberChange = e => {
        let value = e.target.value;
        let parsedNumber = new AsYouType().input(withDialCode(countryCode, value));

        setNumber(value);
        onChange(parsedNumber);
    };

    return (
        <InputGroup size={size} {...rest}>
            {/* The country picker is a transparent native select laid over the flag.
                Chakra's own chevron sits outside that select, which is why it drifted
                away from the flag — it is replaced by one drawn next to the flag. */}
            {(() => {
                const picker = (
                    <>
                        <Select
                            id={"PhoneNumberPrefix"}
                            icon={<></>}
                            iconSize="0"
                            value={selectedCountry}
                            onChange={onCountryChange}
                            position="absolute"
                            top="0"
                            insetStart="0"
                            width="100%"
                            height="100%"
                            opacity={0}
                            zIndex={2}
                            cursor="pointer"
                        >
                            <option key="-1" value="" />
                            {options.map(option => (
                                <option key={option.value} value={option.value}>
                                    {t(`countries.${option.value}`)}
                                </option>
                            ))}
                        </Select>

                        <Flex align="center" justify="center" gap="6px" width="100%" pointerEvents="none">
                            {selectedCountry ? (
                                <Flag
                                    code={selectedCountry || ""}
                                    style={{ width: "24px", height: "16px", borderRadius: "3px", objectFit: "cover", display: "block" }}
                                />
                            ) : (
                                <FaPhoneAlt />
                            )}
                            <ChevronDownIcon boxSize="14px" opacity={0.75} />
                        </Flex>
                    </>
                );

                return lang === 'ar'
                    ? <InputRightElement width="4.4rem" height="100%">{picker}</InputRightElement>
                    : <InputLeftElement width="4.4rem" height="100%">{picker}</InputLeftElement>;
            })()}
            <Input
                dir={lang === 'ar' ? 'rtl' : 'ltr'}
                id={"PhoneNumberInput"}
                type="tel"
                value={number}
                pattern="[0-9]"
                placeholder={placeholder}
                onChange={onPhoneNumberChange}
                /* Physical sides on purpose: the field is dir="rtl" in Arabic, so the
                   logical inline-start flips to the same side the picker sits on and
                   the placeholder ended up underneath the flag. The picker is on the
                   left in English and on the right in Arabic, either way. */
                style={{
                    paddingLeft: lang === 'ar' ? '16px' : '86px',
                    paddingRight: lang === 'ar' ? '86px' : '16px',
                }}
                bg="rgba(255, 255, 255, 0.1)"
                borderColor="rgba(255, 255, 255, 0.3)"
                color="white"
                _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                _hover={{ borderColor: "rgba(255, 255, 255, 0.5)" }}
                _focus={{
                    borderColor: "rgba(255, 255, 255, 0.8)",
                    bg: "rgba(255, 255, 255, 0.15)",
                    boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.3)"
                }}
            />
        </InputGroup>
    );
}

PhoneNumberInput.defaultProps = {
    options: [],
    size: "md"
};
